// ============================================================================
// TENANT CONFIG CACHE — localStorage with TTL/version (issue #49)
// ============================================================================
// Persists the last successful public tenant-config response in `localStorage`
// so the platform boots instantly from cache (stale-while-revalidate) and keeps
// working offline / when the API is unreachable. Each entry stores the payload,
// the response `ETag` (for conditional re-fetch) and the fetch timestamp.
//
// `SCHEMA_VERSION` namespaces the stored shape: bumping it transparently
// invalidates every previously cached entry after a deploy that changes the
// cache layout.
// ============================================================================

import type { PublicTenantConfig } from "./types";

const KEY_PREFIX = "cc:tenantConfig";
const SCHEMA_VERSION = 1;

/** Freshness window — within this age the cache is served without revalidating.
 * Matches the API's `Cache-Control: max-age=60`. */
export const FRESH_TTL_MS = 60_000;

/** How often background revalidation re-checks the API (5 minutes). */
export const REVALIDATE_INTERVAL_MS = 5 * 60_000;

/** A persisted cache entry. */
export interface CacheEntry {
  schemaVersion: number;
  tenantKey: string;
  etag: string | null;
  fetchedAt: number; // epoch ms
  payload: PublicTenantConfig;
}

function storageKey(tenantKey: string): string {
  return `${KEY_PREFIX}:v${SCHEMA_VERSION}:${tenantKey}`;
}

/** localStorage may be unavailable (SSR, private mode, quota). Guard access. */
function safeStorage(): Storage | null {
  try {
    return typeof window !== "undefined" ? window.localStorage : null;
  } catch {
    return null;
  }
}

/** Read the cached entry for a tenant, or null when absent/corrupt/outdated. */
export function readCache(tenantKey: string): CacheEntry | null {
  const store = safeStorage();
  if (!store) return null;
  try {
    const raw = store.getItem(storageKey(tenantKey));
    if (!raw) return null;
    const entry = JSON.parse(raw) as CacheEntry;
    if (
      !entry ||
      entry.schemaVersion !== SCHEMA_VERSION ||
      !entry.payload ||
      !entry.payload.id
    ) {
      return null;
    }
    return entry;
  } catch {
    return null;
  }
}

/** Persist a successful fetch. Failures (quota/private mode) are swallowed. */
export function writeCache(
  tenantKey: string,
  payload: PublicTenantConfig,
  etag: string | null,
  fetchedAt: number = Date.now(),
): void {
  const store = safeStorage();
  if (!store) return;
  const entry: CacheEntry = {
    schemaVersion: SCHEMA_VERSION,
    tenantKey,
    etag,
    fetchedAt,
    payload,
  };
  try {
    store.setItem(storageKey(tenantKey), JSON.stringify(entry));
  } catch {
    // Ignore — running from a non-persisted cache is still functional.
  }
}

/** Remove the cached entry for a tenant. */
export function clearCache(tenantKey: string): void {
  const store = safeStorage();
  if (!store) return;
  try {
    store.removeItem(storageKey(tenantKey));
  } catch {
    // Ignore.
  }
}

/** True when the entry is younger than the freshness window. */
export function isFresh(entry: CacheEntry, now: number = Date.now()): boolean {
  return now - entry.fetchedAt < FRESH_TTL_MS;
}
